import {
	ExpandMore as ExpandMoreIcon,
	NotificationsActive as BellIcon,
	Warning as WarningIcon,
} from '@mui/icons-material'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemText,
	Tab,
	Tabs,
	Typography,
} from '@mui/material'
import React, { useState } from 'react'

import type { FeedbackGridApi } from '../api/feedbackGridApi'

export interface NotificationGroup {
	emails: string[]
	feedbacks: Array<{ id: number }>
	subject: string
	html: string
}

export interface NotificationSettingData {
	settingId: number
	settingName: string
	groups: NotificationGroup[]
}

interface NotificationTestDialogProps {
	open: boolean
	feedbackIds: number[]
	settings: NotificationSettingData[]
	onClose: () => void
	onSent: () => void
	api: FeedbackGridApi
	translations: Record<string, string>
}

const TemplatePreview: React.FC<{ html: string; subject: string }> = ({ html, subject }) => (
	<Box>
		<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
			Subject: <strong>{subject}</strong>
		</Typography>
		<Box
			sx={{
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 1,
				overflow: 'hidden',
				maxHeight: 400,
				overflowY: 'auto',
				bgcolor: 'background.paper',
			}}
		>
			<iframe
				srcDoc={html}
				style={{ width: '100%', minHeight: 300, border: 'none', display: 'block' }}
				sandbox="allow-same-origin"
				title="Email preview"
			/>
		</Box>
	</Box>
)

const GroupPanel: React.FC<{ group: NotificationGroup; index: number; translations: Record<string, string> }> = ({
	group,
	index,
	translations,
}) => (
	<Accordion disableGutters defaultExpanded={false}>
		<AccordionSummary expandIcon={<ExpandMoreIcon />}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography variant="subtitle2">
					{translations.notificationGroup || 'Group'} {index + 1}
				</Typography>
				<Chip
					label={`${group.emails.length} ${translations.notificationRecipients || 'recipients'}`}
					size="small"
					color={group.emails.length === 0 ? 'default' : 'primary'}
				/>
				<Chip label={`${group.feedbacks.length} feedbacks`} size="small" variant="outlined" />
			</Box>
		</AccordionSummary>
		<AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
			{/* Recipients */}
			{group.emails.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
					No recipients found.
				</Typography>
			) : (
				<List dense disablePadding>
					{group.emails.map((email) => (
						<ListItem key={email} sx={{ py: 0.25 }}>
							<ListItemText primary={email} primaryTypographyProps={{ variant: 'body2' }} />
						</ListItem>
					))}
				</List>
			)}

			{/* Feedbacks */}
			<Box>
				<Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
					Feedbacks
				</Typography>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
					{group.feedbacks.map((f) => (
						<Chip key={f.id} label={`#${f.id}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
					))}
				</Box>
			</Box>

			{/* Email preview */}
			<TemplatePreview html={group.html} subject={group.subject} />
		</AccordionDetails>
	</Accordion>
)

const SettingPanel: React.FC<{ setting: NotificationSettingData; translations: Record<string, string> }> = ({
	setting,
	translations,
}) => (
	<Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
		{setting.groups.length === 0 ? (
			<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
				No groups generated for this setting.
			</Typography>
		) : (
			setting.groups.map((group, i) => <GroupPanel key={i} group={group} index={i} translations={translations} />)
		)}
	</Box>
)

export const NotificationTestDialog: React.FC<NotificationTestDialogProps> = ({
	open,
	feedbackIds,
	settings,
	onClose,
	onSent,
	api,
	translations,
}) => {
	const [sending, setSending] = useState(false)
	const [confirmed, setConfirmed] = useState(false)
	const [activeTab, setActiveTab] = useState(0)

	const totalEmails = [...new Set(settings.flatMap((s) => s.groups.flatMap((g) => g.emails)))].length
	const canSend = settings.length > 0 && totalEmails > 0

	const handleSend = async () => {
		if (!confirmed) {
			setConfirmed(true)
			return
		}

		setSending(true)
		try {
			const result = await api.sendBulkNotification(feedbackIds)
			if (result.success) {
				onSent()
			} else {
				alert(result.error || 'Failed to send notifications.')
			}
		} finally {
			setSending(false)
			setConfirmed(false)
		}
	}

	const handleClose = () => {
		setConfirmed(false)
		onClose()
	}

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<BellIcon color="error" />
				{translations.testNotification || 'Test Notification'}
				<Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
					{feedbackIds.length} feedback(s) · {settings.length} setting(s) · {totalEmails} recipient(s)
				</Typography>
			</DialogTitle>

			<DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 500 }}>
				{confirmed && (
					<Alert severity="warning" icon={<WarningIcon />} sx={{ mx: 3, mt: 2 }}>
						{translations.notificationConfirm || 'Are you sure? This will send real emails to the listed recipients.'}
					</Alert>
				)}

				{settings.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ p: 3, fontStyle: 'italic' }}>
						No matching notification settings found for the selected feedbacks.
					</Typography>
				) : (
					<>
						<Tabs
							value={activeTab}
							onChange={(_, v) => setActiveTab(v)}
							variant="scrollable"
							scrollButtons="auto"
							sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
						>
							{settings.map((s, i) => {
								const settingEmails = [...new Set(s.groups.flatMap((g) => g.emails))].length
								return (
									<Tab
										key={s.settingId}
										label={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												{s.settingName}
												<Chip
													label={settingEmails}
													size="small"
													color={settingEmails === 0 ? 'default' : 'error'}
													sx={{ height: 18, fontSize: 11 }}
												/>
											</Box>
										}
										value={i}
									/>
								)
							})}
						</Tabs>

						{settings[activeTab] !== undefined && (
							<Box role="tabpanel" sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 2 }}>
								<SettingPanel
									key={settings[activeTab].settingId}
									setting={settings[activeTab]}
									translations={translations}
								/>
							</Box>
						)}
					</>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
				<Button onClick={handleClose} disabled={sending}>
					{translations.close || 'Close'}
				</Button>
				<Button
					variant="contained"
					color="error"
					startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <BellIcon />}
					onClick={handleSend}
					disabled={sending || !canSend}
				>
					{confirmed ? translations.confirmAndSend || 'Confirm & Send!' : translations.sendNotification || 'Send'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
